if (!customElements.get('product-variant-3d')) {
  customElements.define(
    'product-variant-3d',
    class ProductVariant3d extends HTMLElement {
      constructor() {
        super();
        this.models = {};
        this.viewer = null;
        this.modal = null;
        this.openBtn = null;
        this.priceEl = null;
        this.qrImg = null;
        this.boundVariantChange = this.onVariantChange.bind(this);
        this.boundKeydown = this.onKeydown.bind(this);
        this.autoOpenAttempted = false;
      }

      connectedCallback() {
        const jsonEl = this.querySelector('[data-product-3d-models]');
        if (jsonEl) {
          try {
            this.models = JSON.parse(jsonEl.textContent);
          } catch (e) {
            this.models = {};
          }
        }

        this.viewer = this.querySelector('model-viewer');
        this.modal = this.querySelector('[data-product-3d-modal]');
        this.openBtn = this.querySelector('[data-product-3d-open]');
        this.actions = this.querySelector('.product-variant-3d__actions');
        this.priceEl = this.querySelector('[data-product-3d-price]');
        this.qrImg = this.querySelector('[data-product-3d-qr]');

        this.querySelectorAll('[data-product-3d-open]').forEach((btn) => {
          btn.addEventListener('click', () => this.openModal());
        });
        this.querySelectorAll('[data-product-3d-close]').forEach((btn) => {
          btn.addEventListener('click', () => this.closeModal());
        });

        this.mountChip();
        this.boundResize = this.mountChip.bind(this);
        window.addEventListener('resize', this.boundResize);

        document.addEventListener('product:variant-change', this.boundVariantChange);

        const params = this.getViewParams();
        const initialId = params.variantId || (this.dataset.initialVariant ? Number(this.dataset.initialVariant) : null);
        this.applyVariant(initialId);

        this.maybeAutoOpenFromQuery();
      }

      getViewParams() {
        try {
          const params = new URLSearchParams(window.location.search);
          const view = (params.get('view') || '').toLowerCase();
          const wants3d = view === '3d' || view === 'ar';
          const variantRaw = params.get('variant');
          const variantId = variantRaw ? Number(variantRaw) : null;
          return { wants3d, variantId: Number.isFinite(variantId) ? variantId : null };
        } catch (e) {
          return { wants3d: false, variantId: null };
        }
      }

      buildPreviewUrl(variantId) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('view', '3d');
          if (variantId) url.searchParams.set('variant', String(variantId));
          return url.toString();
        } catch (e) {
          const base = this.dataset.arUrl || window.location.href;
          return base;
        }
      }

      updateQr(variantId) {
        if (!this.qrImg) return;
        const previewUrl = this.buildPreviewUrl(variantId);
        const qrSrc =
          'https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=10&ecc=M&data=' +
          encodeURIComponent(previewUrl);
        if (this.qrImg.getAttribute('src') !== qrSrc) {
          this.qrImg.setAttribute('src', qrSrc);
        }
        this.dataset.arUrl = previewUrl;
      }

      mountChip() {
        if (!this.actions) return;
        const container = this.closest('.product-image-container');
        const slides = container?.querySelectorAll('.product-images__slide');
        if (!slides?.length) return;

        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const chipVisible = !this.hidden && this.hasAttribute('data-has-model');

        // Limpia clones y hosts previos
        container.querySelectorAll('[data-product-3d-chip-clone]').forEach((el) => el.remove());
        container.querySelectorAll('.product-images__slide.is-3d-host').forEach((el) => {
          el.classList.remove('is-3d-host');
        });

        if (isMobile) {
          // Móvil: chip arriba derecha en todas las fotos
          this.actions.hidden = true;
          slides.forEach((slide) => {
            slide.classList.add('is-3d-host');
            const clone = this.actions.cloneNode(true);
            clone.hidden = !chipVisible;
            clone.removeAttribute('style');
            clone.setAttribute('data-product-3d-chip-clone', '');
            clone.querySelectorAll('[data-product-3d-open]').forEach((btn) => {
              btn.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.openModal();
              });
            });
            slide.appendChild(clone);
          });
          return;
        }

        // Desktop: chip en la 2ª foto
        this.actions.hidden = !chipVisible;
        const hostIndex = Math.min(1, slides.length - 1);
        const host = slides[hostIndex];
        if (!host) return;
        host.classList.add('is-3d-host');
        if (this.actions.parentElement !== host) {
          host.appendChild(this.actions);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.boundVariantChange);
        document.removeEventListener('keydown', this.boundKeydown);
        if (this.boundResize) window.removeEventListener('resize', this.boundResize);
      }

      applyVariant(variantId) {
        const entry = variantId != null ? this.models[String(variantId)] : null;
        if (!entry || !entry.src) {
          this.hidden = true;
          this.removeAttribute('data-has-model');
          this.mountChip();
          this.closeModal();
          return;
        }

        this.hidden = false;
        this.setAttribute('data-has-model', 'true');
        this.mountChip();
        this.currentVariantId = variantId;
        this.updateQr(variantId);

        if (this.viewer) {
          if (this.viewer.getAttribute('src') !== entry.src) {
            this.viewer.setAttribute('src', entry.src);
          }
          if (entry.iosSrc) {
            this.viewer.setAttribute('ios-src', entry.iosSrc);
          } else {
            this.viewer.removeAttribute('ios-src');
          }
          if (entry.poster) {
            this.viewer.setAttribute('poster', entry.poster);
          } else {
            this.viewer.removeAttribute('poster');
          }
        }

        if (this.priceEl && entry.price) {
          this.priceEl.textContent = entry.price;
        }
      }

      onVariantChange(event) {
        const sectionId = this.dataset.sectionId;
        if (sectionId && event.detail?.sectionId && String(event.detail.sectionId) !== String(sectionId)) {
          return;
        }
        const variant = event.detail?.variant;
        this.applyVariant(variant ? variant.id : null);
      }

      openModal() {
        if (!this.modal || this.hidden) return false;
        this.modal.hidden = false;
        document.documentElement.classList.add('product-variant-3d-open');
        document.addEventListener('keydown', this.boundKeydown);
        const closeBtn = this.modal.querySelector('.product-variant-3d__close');
        if (closeBtn) closeBtn.focus();
        return true;
      }

      maybeAutoOpenFromQuery() {
        const { wants3d } = this.getViewParams();
        if (!wants3d || this.autoOpenAttempted) return;

        const tryOpen = (attempt = 0) => {
          if (this.openModal()) {
            this.autoOpenAttempted = true;
            this.cleanViewParamFromUrl();
            return;
          }
          if (attempt < 20) {
            setTimeout(() => tryOpen(attempt + 1), 100);
          }
        };

        requestAnimationFrame(() => tryOpen(0));
      }

      cleanViewParamFromUrl() {
        try {
          const url = new URL(window.location.href);
          if (!url.searchParams.has('view')) return;
          url.searchParams.delete('view');
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          /* ignore */
        }
      }

      closeModal() {
        if (!this.modal || this.modal.hidden) return;
        this.modal.hidden = true;
        document.documentElement.classList.remove('product-variant-3d-open');
        document.removeEventListener('keydown', this.boundKeydown);
        if (this.openBtn) this.openBtn.focus();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.closeModal();
      }
    }
  );
}
